import { QueryClient, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { StatusProcessamentoIA } from "@workflow/types";
import { PageResponse } from "@/types/http.type";
import { ProcessamentoIADTO } from "@/types/processamento-ia.type";
import { processamentoIAService } from "@/services/api/processamento-ia.service";
import { processamentoIaKeys } from "@/services/api/queries/processamento-ia.queries";

const LIST_KEY = processamentoIaKeys.lists();
const RESUMO_KEY = processamentoIaKeys.resumo;
const detailsKey = processamentoIaKeys.detail;

/** Estado otimista de "executando": volta para PENDENTE e limpa o retorno. */
function toPendente(p: ProcessamentoIADTO): ProcessamentoIADTO {
  return {
    ...p,
    status: StatusProcessamentoIA.PENDENTE,
    resultado: undefined,
    mensagemErro: undefined,
  };
}

/**
 * Marca os IDs como PENDENTE imediatamente (lista + detalhes) e devolve um
 * snapshot para rollback caso a requisição falhe.
 */
async function aplicarOtimista(queryClient: QueryClient, ids: number[]) {
  const idSet = new Set(ids);

  // Evita que um fetch em voo sobrescreva o estado otimista.
  await queryClient.cancelQueries({ queryKey: LIST_KEY });
  await Promise.all(ids.map((id) => queryClient.cancelQueries({ queryKey: detailsKey(id) })));

  const listasAnteriores = queryClient.getQueriesData<PageResponse<ProcessamentoIADTO>>({
    queryKey: LIST_KEY,
  });
  const detalhesAnteriores = ids.map(
    (id) => [detailsKey(id), queryClient.getQueryData(detailsKey(id))] as const,
  );

  queryClient.setQueriesData<PageResponse<ProcessamentoIADTO>>({ queryKey: LIST_KEY }, (old) => {
    if (!old?.data) return old;
    return { ...old, data: old.data.map((p) => (idSet.has(p.id) ? toPendente(p) : p)) };
  });

  ids.forEach((id) => {
    queryClient.setQueryData<ProcessamentoIADTO>(detailsKey(id), (old) =>
      old ? toPendente(old) : old,
    );
  });

  return { listasAnteriores, detalhesAnteriores };
}

type SnapshotOtimista = Awaited<ReturnType<typeof aplicarOtimista>>;

function reverter(queryClient: QueryClient, ctx?: SnapshotOtimista) {
  ctx?.listasAnteriores.forEach(([key, data]) => queryClient.setQueryData(key, data));
  ctx?.detalhesAnteriores.forEach(([key, data]) => queryClient.setQueryData(key, data));
}

/** Revalida lista, KPIs e os detalhes dos IDs envolvidos contra o servidor. */
function reconciliar(queryClient: QueryClient, ids: number[]) {
  queryClient.invalidateQueries({ queryKey: LIST_KEY });
  queryClient.invalidateQueries({ queryKey: RESUMO_KEY });
  ids.forEach((id) => queryClient.invalidateQueries({ queryKey: detailsKey(id) }));
}

/**
 * Reprocessa um registro existente (limpa o retorno e reenvia à IA no mesmo
 * registro). A UI já mostra "executando" otimisticamente e faz rollback em erro.
 */
export function useReprocessarProcessamentoIA() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => processamentoIAService.reprocessar(id),
    onMutate: (id) => aplicarOtimista(queryClient, [id]),
    onError: (_error, _id, ctx) => reverter(queryClient, ctx),
    onSuccess: (_data, id) => toast.success(`Processamento #${id} reenviado para a IA.`),
    onSettled: (_data, _error, id) => reconciliar(queryClient, [id]),
  });
}

/**
 * Reprocessa em lote os IDs selecionados, com resumo de sucessos/falhas.
 * Também aplica o estado otimista e reverte tudo se a requisição falhar por
 * completo (falhas parciais são corrigidas na reconciliação).
 */
export function useReprocessarLoteProcessamentoIA() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: number[]) => processamentoIAService.reprocessarLote(ids),
    onMutate: (ids) => aplicarOtimista(queryClient, ids),
    onError: (_error, _ids, ctx) => reverter(queryClient, ctx),
    onSuccess: ({ reprocessados, falhas }) => {
      if (reprocessados.length > 0) {
        toast.success(`${reprocessados.length} processamento(s) reenviado(s) para a IA.`);
      }
      if (falhas.length > 0) {
        toast.error(`${falhas.length} não puderam ser reprocessados.`);
      }
    },
    onSettled: (_data, _error, ids) => reconciliar(queryClient, ids),
  });
}
